/**
 * schemas.ts
 *
 * Zod schemas (and their inferred TypeScript types) for every entity and
 * API request/response body in the CMS. apps/worker's route handlers parse
 * incoming bodies with `<schema>.parse()` and shape outgoing JSON to match
 * these shapes; apps/admin's API client (apps/admin/src/lib/api.ts) parses
 * every response with the same schemas. Because both apps import from this
 * one file, the request/response contract cannot silently drift between
 * frontend and backend.
 *
 * Naming convention:
 *   `<Entity>Schema`        — a full row as stored/returned (includes id, timestamps)
 *   `create<Entity>Schema`  — body accepted by a POST that creates one
 *   `update<Entity>Schema`  — body accepted by a PATCH that updates one
 */
import { siteLayoutSchema, isBlogLayout, blogPublishBlockers, type SiteLayout } from "./siteLayout.ts";
import { z } from "zod";
import { aiProviderSchema } from "./ai.ts";
import { isValidTimeZone } from "./timezone.ts";
import {
  auditActionSchema,
  auditTargetTypeSchema,
  capabilitySchema,
  contentStatusSchema,
  contentTypeSchema,
  publishTargetSchema,
  roleSchema,
  type ContentStatus,
  type ContentType,
} from "./enums.ts";

/**
 * Audit finding H2: a content/author/media slug is spliced directly into a
 * real GitHub commit path and public URL segment (see
 * `apps/worker/src/lib/frontmatter.ts`'s `contentFilePath`/
 * `contentPublicUrl`, `authorContentFilePath`, and
 * `apps/worker/src/routes/media.ts`'s `mediaRepoPath` — the last of which
 * already sanitizes the *filename* but relies on the *slug* being safe,
 * proving this was a gap rather than a deliberate design choice) — so it
 * needs the same lowercase/number/hyphen constraint `createSiteSchema.id`
 * already uses, not a bare `z.string().min(1)` that would accept
 * `../../../etc/passwd`-style path segments or raw spaces/punctuation that
 * would produce a broken public URL. Applied only to the INPUT schemas
 * below (what a client may submit) — never to a *Schema read/output shape,
 * which must keep accepting whatever is already stored, including any
 * historically-imported slug that predates this constraint.
 */
const slugSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers, and hyphens only (e.g. \"my-article-title\")");

// ---------------------------------------------------------------------------
// Site
// ---------------------------------------------------------------------------

/**
 * A site the CMS publishes content for. Every content row, tag, redirect,
 * and audit entry is scoped by `site_id`. Adding a second site is just
 * inserting a new row here — no migration required, per the client's
 * multi-site requirement.
 */
export const siteSchema = z.object({
  id: z.string().min(1), // human-chosen slug id, e.g. "demandhub"
  name: z.string().min(1),
  /** The site's PRODUCTION domain, e.g. "https://demandhub.co" — combined
   * with `contentPublicPath()`/`contentLiveUrl()` (mediaPaths.ts) to build a
   * content item's real, clickable live URL once it's published to
   * production. */
  domain: z.string().min(1),
  /** Bug fix ("content should also have live link, staging or prod domain
   * should be set and correct valid should be visible"): there was no way
   * to configure a STAGING domain at all — only the single (production)
   * `domain` field existed, even though every publish action already has a
   * real staging/production distinction (`publishTarget`, `stagingBranch`/
   * `productionBranch`, two separate deploy hooks). Nullable because a
   * brand-new site may not have a staging domain provisioned yet (or may
   * intentionally share environments); "View live" links only render for
   * whichever branch(es) actually have a domain configured. */
  stagingDomain: z.string().min(1).nullable(),
  githubRepo: z.string().min(1).nullable(), // "owner/repo"
  productionBranch: z.string().min(1),
  stagingBranch: z.string().min(1),
  productionDeployHookUrl: z.string().url().nullable(),
  stagingDeployHookUrl: z.string().url().nullable(),
  imagesBasePath: z.string().min(1), // e.g. "src/assets/images/demandhub"
  /** v2: when false (the default) the cron never publishes on its own;
   * scheduled items wait with a visible reason until a manager turns this on
   * or uses "Publish now". */
  autoPublishEnabled: z.boolean(),
  /** IANA timezone name (e.g. "Asia/Karachi"). Daily/weekly limits and the calendar's days follow it. Stored timestamps stay UTC. */
  timezone: z.string().refine(isValidTimeZone, "Choose a timezone from the list, e.g. Asia/Karachi or UTC.").default("UTC"),
  /** First day of the week: 1 = Monday, 0 = Sunday. */
  weekStartsOn: z.union([z.literal(0), z.literal(1)]).default(1),
  /** Evidence store on: the Evidence page works and the AI is given this site's evidence. OFF by default. */
  evidenceEnabled: z.boolean().default(false),
  /** v2: max automatic publishes per day / week in the site's timezone (see publishEngine.ts). */
  publishDailyCap: z.number().int().min(0).max(100),
  publishWeeklyCap: z.number().int().min(0).max(500),
  /** v2: which LLM provider the AI Worker uses for this site (default anthropic); model null = the AI Worker's configured default. */
  aiProvider: aiProviderSchema,
  aiModel: z.string().min(1).max(100).nullable(),
  /** How posts are laid out in the site's repository. null = the built-in DemandHub layout (see siteLayout.ts). */
  contentLayout: siteLayoutSchema.nullable().default(null),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Site = z.infer<typeof siteSchema>;

export const updateSiteSchema = siteSchema
  .pick({
    name: true,
    domain: true,
    stagingDomain: true,
    githubRepo: true,
    productionBranch: true,
    stagingBranch: true,
    productionDeployHookUrl: true,
    stagingDeployHookUrl: true,
    imagesBasePath: true,
    autoPublishEnabled: true,
    timezone: true,
    weekStartsOn: true,
    evidenceEnabled: true,
    publishDailyCap: true,
    publishWeeklyCap: true,
    aiProvider: true,
    aiModel: true,
    contentLayout: true,
  })
  .partial();
export type UpdateSiteInput = z.infer<typeof updateSiteSchema>;

/**
 * Body accepted by POST /sites (manager-only). `id` is a human-chosen slug
 * (lowercase letters/numbers/hyphens — it's used as a primary key and as
 * the default images base path segment, so it's kept URL/path-safe rather
 * than accepting arbitrary text). Everything else defaults sensibly so a
 * manager can create a new site with just a name and domain and configure
 * repo/branches later from Git & deploy settings.
 */
export const createSiteSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "Use lowercase letters, numbers, and hyphens only"),
  name: z.string().min(1),
  domain: z.string().min(1),
  stagingDomain: z.string().min(1).nullable().optional(),
  githubRepo: z.string().min(1).nullable().optional(),
  productionBranch: z.string().min(1).optional(),
  stagingBranch: z.string().min(1).optional(),
  imagesBasePath: z.string().min(1).optional(),
});
export type CreateSiteInput = z.infer<typeof createSiteSchema>;

// ---------------------------------------------------------------------------
// User
// ---------------------------------------------------------------------------

/**
 * A CMS user. Auth (password hash, sessions) is owned entirely by Better
 * Auth's own tables (see apps/worker/src/db/schema.ts) — this schema
 * describes the CMS-facing view of a user plus the `role` field the CMS
 * adds on top of Better Auth's base user table via additionalFields.
 */
export const userSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  name: z.string().min(1),
  role: roleSchema,
  isActive: z.boolean(),
  createdAt: z.string(),
  /** Two-step sign-in is on for this person. */
  mfaEnabled: z.boolean().default(false),
  /** Invited, but has not chosen a password or signed in yet. */
  pendingInvite: z.boolean().default(false),
  /** Sites this person may open (managers open all, so the list is empty for them). */
  siteIds: z.array(z.string()).default([]),
});
export type User = z.infer<typeof userSchema>;

/** What happened to the set-password link: emailed, or (when email is not set up / failed) handed to the manager once. */
export const inviteLinkResultSchema = z.object({
  emailed: z.boolean(),
  /** Plain-language reason when it was not emailed. */
  detail: z.string().nullable(),
  /** One-time set-password link, only present when the email could not be sent. */
  link: z.string().nullable(),
});
export type InviteLinkResult = z.infer<typeof inviteLinkResultSchema>;
export const inviteUserResponseSchema = userSchema.extend({ invite: inviteLinkResultSchema });
export type InviteUserResponse = z.infer<typeof inviteUserResponseSchema>;

export const inviteUserSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  name: z.string().trim().min(1).max(120),
  role: roleSchema,
  /** Sites the new person may open. Ignored for managers. */
  siteIds: z.array(z.string().min(1)).max(200).default([]),
});
export type InviteUserInput = z.infer<typeof inviteUserSchema>;

export const setUserSitesSchema = z.object({ siteIds: z.array(z.string().min(1)).max(200) });
export type SetUserSitesInput = z.infer<typeof setUserSitesSchema>;

export const updateUserRoleSchema = z.object({ role: roleSchema });
export type UpdateUserRoleInput = z.infer<typeof updateUserRoleSchema>;

/** Query params accepted by GET /users (list with pagination + search). */
export const listUsersQuerySchema = z.object({
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

export const listUsersResponseSchema = z.object({
  items: z.array(userSchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
});
export type ListUsersResponse = z.infer<typeof listUsersResponseSchema>;

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

export const tagSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  createdAt: z.string(),
});
export type Tag = z.infer<typeof tagSchema>;

export const createTagSchema = z.object({
  siteId: z.string().min(1),
  name: z.string().min(1),
});
export type CreateTagInput = z.infer<typeof createTagSchema>;

// ---------------------------------------------------------------------------
// Categories (article `category` frontmatter key -> article_categories.json)
// ---------------------------------------------------------------------------

/**
 * Real evidence: articles' frontmatter `category` key indexes into
 * `article_categories.json` ({ [key]: { name } }) in the Astro repo. We
 * don't have that file's full real contents (only 3 confirmed values +
 * "all" — see migrations/0003_seed_categories_authors.sql), so this is a
 * normal CMS-managed table a Manager can extend via Settings, not a fixed
 * enum.
 */
export const categorySchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1),
  key: z.string().min(1), // lowercase key written into frontmatter, e.g. "automotive"
  name: z.string().min(1), // display name
  createdAt: z.string(),
});
export type Category = z.infer<typeof categorySchema>;

export const createCategorySchema = z.object({
  siteId: z.string().min(1),
  key: z.string().min(1),
  name: z.string().min(1),
});
export type CreateCategoryInput = z.infer<typeof createCategorySchema>;

export const updateCategorySchema = z.object({
  key: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
});
export type UpdateCategoryInput = z.infer<typeof updateCategorySchema>;

/**
 * Query params for GET /categories (bug fix: "there is no pagination" —
 * Content → Categories & Authors always fetched and rendered the ENTIRE
 * table with no way to page through it). `page`/`pageSize` are both
 * OPTIONAL and undefined by default (not `.default(1)`-style Zod
 * defaults): other callers — most importantly ContentEditor.tsx's
 * category/author pickers, which need the FULL list to search/select from,
 * not one page of it — call GET /categories and GET /authors with no
 * pagination params at all and must keep getting every row back
 * unpaginated, exactly as before this fix. Only when a caller (the
 * Categories & Authors settings screen) explicitly sends `page`/`pageSize`
 * does the route apply LIMIT/OFFSET.
 */
export const listCategoriesQuerySchema = z.object({
  siteId: z.string().min(1).optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional(),
});
export type ListCategoriesQuery = z.infer<typeof listCategoriesQuerySchema>;

export const listCategoriesResponseSchema = z.object({
  items: z.array(categorySchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
});
export type ListCategoriesResponse = z.infer<typeof listCategoriesResponseSchema>;

// ---------------------------------------------------------------------------
// Site variables — site-wide values referenced from content bodies as
// `{{var.<key>}}` (e.g. a price). Editing a variable's value re-publishes
// (re-commits) every published item that references it, resolving the
// placeholder into the new value at commit time — see
// docs/ARCHITECTURE.md's "Site variables" section for the full design and
// apps/worker/src/lib/variables.ts for the resolve/extract functions.
// ---------------------------------------------------------------------------

export const siteVariableSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1),
  key: z.string().min(1), // referenced as {{var.<key>}}, e.g. "pricing_basic"
  label: z.string().min(1),
  value: z.string(),
  description: z.string().nullable(),
  createdBy: z.string().min(1).nullable(),
  updatedBy: z.string().min(1).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SiteVariable = z.infer<typeof siteVariableSchema>;

export const createSiteVariableSchema = z.object({
  siteId: z.string().min(1),
  key: z.string().min(1),
  label: z.string().min(1),
  value: z.string(),
  description: z.string().nullable().optional(),
});
export type CreateSiteVariableInput = z.infer<typeof createSiteVariableSchema>;

export const updateSiteVariableSchema = z.object({
  label: z.string().min(1).optional(),
  value: z.string().optional(),
  description: z.string().nullable().optional(),
});
export type UpdateSiteVariableInput = z.infer<typeof updateSiteVariableSchema>;

/** Response shape for PATCH /variables/:id — includes how many published
 * items were swept (re-committed) as a result of the value change, so the
 * admin UI can confirm what just happened instead of the value silently
 * changing with no visible consequence. */
export const updateSiteVariableResponseSchema = z.object({
  variable: siteVariableSchema,
  republished: z.number().int().min(0),
  republishFailures: z.array(z.object({ contentId: z.string(), title: z.string(), error: z.string() })),
});
export type UpdateSiteVariableResponse = z.infer<typeof updateSiteVariableResponseSchema>;

// ---------------------------------------------------------------------------
// Role permission matrix (item 7 — granular RBAC controls beyond the fixed
// editor/manager switch). `manager` is intentionally absent from this
// matrix: its capabilities are hardcoded to "all" server-side (see
// requireCapability in apps/worker/src/lib/auth.ts) so a manager can never
// accidentally revoke their own access to the one screen that could undo
// the mistake. Only `editor` and `publisher` rows are ever read or written
// here — see docs/ARCHITECTURE.md's "Round 2 — granular RBAC" section.
// ---------------------------------------------------------------------------

export const rolePermissionSchema = z.object({
  role: roleSchema,
  capability: capabilitySchema,
  allowed: z.boolean(),
});
export type RolePermission = z.infer<typeof rolePermissionSchema>;

export const updateRolePermissionSchema = z.object({
  role: roleSchema,
  capability: capabilitySchema,
  allowed: z.boolean(),
});
export type UpdateRolePermissionInput = z.infer<typeof updateRolePermissionSchema>;

// ---------------------------------------------------------------------------
// Authors
// ---------------------------------------------------------------------------

/**
 * Real evidence: author records in the Astro repo are objects keyed by
 * slug, each with meta_title/meta_description/name/designation/summary/
 * expertise/linkedin_url/category/profile_picture — `expertise` (free text)
 * and `category` (a plain string, e.g. "automotive"/"healthcare"/"all";
 * NOT a foreign key — the real JSON stores a bare string, and "all" isn't
 * any single category row) were added in migration 0006 once
 * src/data/authors.json's real contents were directly inspected.
 */
export const authorSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1),
  /** Client-facing identity: the real layout resolves an article's plain
   * `author` display-name string to this slug via
   * `author.toLowerCase().replace(/\s+/g, "-")`. Kept in sync with `name`
   * by the admin UI, but stored explicitly rather than re-derived, so a
   * later name edit doesn't silently change a slug URLs may depend on. */
  slug: z.string().min(1),
  name: z.string().min(1),
  designation: z.string().nullable(),
  bio: z.string().nullable(), // real JSON's `summary`
  // Free text on purpose: the live site stores LinkedIn as "linkedin.com/in/name/"
  // (no https://). Requiring a full URL made imported authors fail to load
  // ("unexpected response") and made typing the site's own format fail to save.
  linkedinUrl: z.string().nullable(),
  profilePicturePath: z.string().nullable(), // real JSON's `profile_picture`
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  expertise: z.string().nullable(),
  category: z.string().nullable(),
  createdAt: z.string(),
});
export type Author = z.infer<typeof authorSchema>;

export const createAuthorSchema = z.object({
  siteId: z.string().min(1),
  slug: slugSchema.optional(), // auto-derived from name when omitted; audit finding H2
  name: z.string().min(1),
  designation: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  linkedinUrl: z.string().trim().max(300).nullable().optional(),
  profilePicturePath: z.string().nullable().optional(),
  metaTitle: z.string().nullable().optional(),
  metaDescription: z.string().nullable().optional(),
  expertise: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
});
export type CreateAuthorInput = z.infer<typeof createAuthorSchema>;

export const updateAuthorSchema = z.object({
  slug: slugSchema.optional(), // audit finding H2
  name: z.string().min(1).optional(),
  designation: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  linkedinUrl: z.string().trim().max(300).nullable().optional(),
  profilePicturePath: z.string().nullable().optional(),
  metaTitle: z.string().nullable().optional(),
  metaDescription: z.string().nullable().optional(),
  expertise: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
});
export type UpdateAuthorInput = z.infer<typeof updateAuthorSchema>;

/** Query params for GET /authors — same optional-pagination design as
 * listCategoriesQuerySchema above (see its doc comment): omitted
 * page/pageSize means "return everything," matching every existing caller
 * (ContentEditor.tsx's author picker) exactly; a caller that opts in (the
 * Categories & Authors settings screen) gets real LIMIT/OFFSET pagination. */
export const listAuthorsQuerySchema = z.object({
  siteId: z.string().min(1).optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional(),
});
export type ListAuthorsQuery = z.infer<typeof listAuthorsQuerySchema>;

export const listAuthorsResponseSchema = z.object({
  items: z.array(authorSchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
});
export type ListAuthorsResponse = z.infer<typeof listAuthorsResponseSchema>;

// ---------------------------------------------------------------------------
// Redirects
// ---------------------------------------------------------------------------

export const redirectSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1),
  fromPath: z.string().min(1),
  toPath: z.string().min(1),
  createdAt: z.string(),
  /** Set when a redirect was auto-created by a slug change, referencing the content row. */
  contentId: z.string().nullable(),
});
export type Redirect = z.infer<typeof redirectSchema>;

export const createRedirectSchema = z.object({
  siteId: z.string().min(1),
  fromPath: z.string().min(1),
  toPath: z.string().min(1),
});
export type CreateRedirectInput = z.infer<typeof createRedirectSchema>;

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

/** Real evidence: article Props `faq?: {question, answer}[]`. */
export const faqItemSchema = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
});
export type FaqItem = z.infer<typeof faqItemSchema>;

/**
 * Real evidence: article Props `related?: {url, title, thumbnail_url}[]`.
 * This is the RESOLVED, output shape returned by the API — `url`/`title`/
 * `thumbnailUrl` are read live from the referenced content row at request/
 * serialize time (see apps/worker/src/lib/frontmatter.ts), never stored as
 * free text, so a related link can't go stale.
 */
export const relatedContentItemSchema = z.object({
  contentId: z.string().min(1),
  url: z.string().min(1),
  title: z.string().min(1),
  thumbnailUrl: z.string().nullable(),
});
export type RelatedContentItem = z.infer<typeof relatedContentItemSchema>;

/** Resolved author reference embedded in a content item's API response. */
export const contentAuthorRefSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  name: z.string().min(1),
});
export type ContentAuthorRef = z.infer<typeof contentAuthorRefSchema>;

/** Resolved category reference embedded in a content item's API response. */
export const contentCategoryRefSchema = z.object({
  id: z.string().min(1),
  key: z.string().min(1),
  name: z.string().min(1),
});
export type ContentCategoryRef = z.infer<typeof contentCategoryRefSchema>;

/**
 * Every frontmatter field confirmed from the live DemandHub Astro codebase,
 * plus the CMS's own lifecycle/SEO/workflow fields. This is the full shape
 * of a content row as returned by the API.
 *
 * IMPORTANT (real, confirmed inconsistency — do not "fix" it): article and
 * case_study frontmatter use different, non-unifiable field sets and even
 * different casing conventions (article: snake_case meta_title/
 * og_image_url/title_image_url/date_modified; case_study: camelCase
 * contentTitle/titleImageUrl/ogImage). This row shape is the CMS's own
 * internal representation; apps/worker/src/lib/frontmatter.ts's per-type
 * serializers translate it into each type's real, exact frontmatter keys
 * at commit time — the translation lives in exactly one place.
 */
export const contentSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1),
  type: contentTypeSchema,
  status: contentStatusSchema,

  // --- Identity / routing ---
  slug: z.string().min(1),

  // --- SEO fields ---
  // Read side is loose (no min length): imported content may lack these; the publish gate (getContentPublishBlockers) enforces them.
  metaTitle: z.string(),
  metaDescription: z.string(),
  ogImagePath: z.string().nullable(),
  // Read side is loose so imported content with a relative/odd canonical still loads; input is strict (see contentInputSchema).
  canonicalUrl: z.string().nullable(),

  // --- On-page frontmatter fields (distinct from the SEO/meta fields above) ---
  /** Astro frontmatter `title` — historically doubles as the browser <title> input in the site's templates; kept distinct from metaTitle for exact frontmatter parity. */
  title: z.string(),
  /** Astro frontmatter `description` (meta description) */
  description: z.string(),
  /** Astro frontmatter `contentTitle` (case_study only) — the on-page H1, separate from title/metaTitle */
  contentTitle: z.string().nullable(),
  /** Astro frontmatter `contentDescription` (case_study only) — the on-page intro paragraph, separate from description/metaDescription */
  contentDescription: z.string().nullable(),
  /** Astro frontmatter `keywords` (case_study) / `meta_keywords` (article)
   * — comma-separated or free text, stored as-is. One column serves both
   * types' real (differently-named) keywords field. */
  keywords: z.string().nullable(),
  /** Resolved author reference. Real evidence: the real `author` frontmatter
   * value is just a display-name string resolved client-side to a slug —
   * the CMS stores a proper FK to `authors` and the serializer emits
   * `authors.name` as that string, so the value written to Markdown is
   * still exactly what the real layouts expect. */
  authorId: z.string().nullable(),
  author: contentAuthorRefSchema.nullable(),
  /** Resolved category reference (article only). See `categories` table. */
  categoryId: z.string().nullable(),
  category: contentCategoryRefSchema.nullable(),
  /** Astro frontmatter `publishDate` (case_study) / `date` (article) — ISO date string */
  publishDate: z.string().nullable(),
  /** Astro frontmatter `date_modified` (article only) */
  dateModified: z.string().nullable(),
  /** Astro frontmatter `ogImage` (case_study) — repo-relative path, distinct from ogImagePath (CMS SEO field) kept for 1:1 frontmatter fidelity; the two are kept in sync by the editor UI but stored separately so raw frontmatter export is lossless */
  ogImage: z.string().nullable(),
  /** Astro frontmatter `titleImageUrl` (case_study) / `title_image_url` (article) */
  titleImageUrl: z.string().nullable(),
  /** Real evidence (migration 0006): article frontmatter/articles.json
   * `thumbnail_url` — a genuinely different image from title_image_url in
   * every real sample, used for listing-page cards. Article only. */
  thumbnailUrl: z.string().nullable(),
  /** Astro frontmatter `logoImageUrl` (case_study only) */
  logoImageUrl: z.string().nullable(),
  /** Astro frontmatter `snapshotImageUrl` (case_study only) */
  snapshotImageUrl: z.string().nullable(),
  /** Astro frontmatter `heroVideo` (case_study only) — e.g. a Loom embed URL */
  heroVideo: z.string().nullable(),
  /** Real evidence (migration 0006): src/data/case_studies.json's
   * `company_name` — never in the .md frontmatter, only that index file.
   * Case_study only. */
  companyName: z.string().nullable(),
  /** Real evidence (migration 0006): src/data/case_studies.json's
   * `verticals: string[]` — same story as companyName. Case_study only. */
  verticals: z.array(z.string()).nullable(),
  /** Server-computed on every save from bodyMarkdown (word count / 225wpm,
   * rounded) — article only, never an editor input. See frontmatter.ts. */
  readMinutes: z.number().int().nullable(),
  /** Real evidence: article Props `faq?: {question, answer}[]` */
  faq: z.array(faqItemSchema).nullable(),
  /** Real evidence: article Props `related?: {url, title, thumbnail_url}[]`,
   * resolved live from content_related + the target content's own fields. */
  related: z.array(relatedContentItemSchema).default([]),
  /** Real evidence: ebooks.json's `ebook_url` — in every real entry this is
   * a bare filename (e.g. "my-ebook.pdf"), not a full URL; the site's own
   * front-end code resolves it against a fixed CDN prefix. Ebook only. */
  downloadUrl: z.string().nullable(),
  /** Real evidence (migration 0006): ebooks.json's `highlights: string[]`
   * — a short bullet list on the ebook landing page. Ebook only. */
  highlights: z.array(z.string()).nullable(),
  /** Real evidence (migration 0006): ebooks.json's boolean `noindex`/
   * `nofollow` SEO flags. Ebook only. */
  noindex: z.boolean().nullable(),
  nofollow: z.boolean().nullable(),

  // --- Body ---
  bodyMarkdown: z.string(),

  // --- Workflow / publishing ---
  publishTarget: publishTargetSchema.nullable(),
  scheduledFor: z.string().nullable(), // ISO timestamp; set when status = scheduled
  /** Calendar planning date (drafts / in review). Not a schedule; never publishes. */
  plannedFor: z.string().nullable().default(null),
  publishedAt: z.string().nullable(),
  currentVersionId: z.string().nullable(),
  /** v2 publish engine bookkeeping — server-written only, never accepted as input. */
  publishAttempts: z.number().int().min(0),
  publishNextAttemptAt: z.string().nullable(),
  publishLastError: z.string().nullable(),
  /** Why a scheduled item is waiting instead of publishing (auto-publish off, cap, stale approval...). */
  publishBlockedReason: z.string().nullable(),
  /** Free-text campaign tag used by the calendar to group content. */
  campaign: z.string().max(80).nullable(),

  // Nullable: a user account can be deleted (DELETE /users/:id), which
  // sets these to null via the DB's ON DELETE SET NULL rather than
  // blocking the delete — see schema.ts's content table for the full
  // reasoning.
  createdBy: z.string().min(1).nullable(),
  updatedBy: z.string().min(1).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),

  tags: z.array(tagSchema).default([]),
});
export type Content = z.infer<typeof contentSchema>;

/** Fields an editor supplies when creating or editing a draft. Lifecycle
 * fields (status, publishedAt, etc.) are never accepted from the client —
 * they are only changed by the dedicated transition endpoints below.
 * `readMinutes` is deliberately absent — it's always server-computed. */
export const contentInputSchema = contentSchema
  .pick({
    siteId: true,
    type: true,
    slug: true,
    metaTitle: true,
    metaDescription: true,
    ogImagePath: true,
    canonicalUrl: true,
    title: true,
    description: true,
    contentTitle: true,
    contentDescription: true,
    keywords: true,
    authorId: true,
    categoryId: true,
    publishDate: true,
    dateModified: true,
    ogImage: true,
    titleImageUrl: true,
    thumbnailUrl: true,
    logoImageUrl: true,
    snapshotImageUrl: true,
    heroVideo: true,
    companyName: true,
    verticals: true,
    faq: true,
    downloadUrl: true,
    highlights: true,
    noindex: true,
    nofollow: true,
    bodyMarkdown: true,
  })
  .extend({
    canonicalUrl: z.string().trim().url("Canonical URL must be a full web address starting with https://").nullable(),
    /** v2 calendar: optional campaign tag (editors may set it; blank = none). */
    campaign: z.string().trim().max(80).nullable().optional(),
    /** Ids of other content items to link as `related[]` (article only),
     * in display order. Replaces the existing set on write, same pattern
     * as `tagIds` below. */
    relatedContentIds: z.array(z.string()).default([]),
    // Audit finding H2 — see slugSchema's comment above.
    slug: slugSchema,
  });
export type ContentInput = z.infer<typeof contentInputSchema>;

export const createContentSchema = contentInputSchema;
export type CreateContentInput = z.infer<typeof createContentSchema>;

export const updateContentSchema = contentInputSchema
  .omit({ siteId: true, type: true })
  .partial()
  .extend({
    /** Tag ids to set on the content item (replaces the existing set). */
    tagIds: z.array(z.string()).optional(),
    /**
     * Optimistic-concurrency guard (real-world gap fix: this CMS had no
     * protection at all against two people editing the same draft at once
     * — the second PATCH to land silently overwrote the first person's
     * changes with no warning, a classic multi-author CMS bug). The admin
     * UI sends back the item's `updatedAt` from when it loaded the form;
     * if that no longer matches the row's CURRENT `updatedAt`, someone
     * else saved in between and the server rejects this write with a
     * "STALE_EDIT" conflict instead of silently clobbering it. Optional so
     * older/other callers that don't send it keep working unchanged.
     */
    expectedUpdatedAt: z.string().optional(),
  });
export type UpdateContentInput = z.infer<typeof updateContentSchema>;

/** Body for POST /content/:id/publish — manager-only. */
export const publishContentSchema = z.object({
  target: publishTargetSchema,
});
export type PublishContentInput = z.infer<typeof publishContentSchema>;

/** Body for POST /content/:id/schedule — manager-only. */
export const scheduleContentSchema = z.object({
  target: publishTargetSchema,
  // Audit finding M2: this was a bare `z.string()`, so an unparseable
  // value (a typo, a client bug) passed validation, and the route's own
  // "must be in the future" guard (`new Date(x).getTime() <= Date.now()`)
  // silently let it through too, because `NaN <= anything` is `false` in
  // JS. The item then sat permanently stuck in `scheduled` status: the
  // cron's due-item lookup is a lexicographic TEXT comparison on this
  // column, and a non-date string sorts after any real ISO date, so it
  // was never picked up. `apps/admin/src/pages/ContentEditor.tsx` always
  // sends `new Date(...).toISOString()`, which `.datetime()` accepts.
  scheduledFor: z.string().datetime({ message: "scheduledFor must be a valid ISO 8601 UTC timestamp." }),
});
export type ScheduleContentInput = z.infer<typeof scheduleContentSchema>;

/** Body for POST /content/:id/reject — manager-only, sends a pending_review item back to draft. */
export const rejectContentSchema = z.object({
  reason: z.string().min(1).optional(),
});
export type RejectContentInput = z.infer<typeof rejectContentSchema>;

/**
 * getContentPublishBlockers — the single source of truth for "is this item
 * actually ready to go live," shared by apps/worker's POST /:id/publish +
 * POST /:id/schedule (server-side gate, returns 400 if non-empty) and
 * apps/admin's ContentEditor (client-side: disables the Publish/Schedule
 * buttons and lists the same reasons before the confirmation dialog even
 * opens). One rule set, enforced in both places, so there's no route to
 * publishing genuinely empty content — which is exactly the bug reported:
 * "if i publish empty it go through."
 *
 * Deliberately just "is this field blank," not a minimum length/word
 * count — that would risk rejecting legitimately short, real content. The
 * bug being fixed is *empty* content going live, not *short* content.
 */
export interface ContentPublishCheckInput {
  type: ContentType;
  title: string;
  metaTitle: string;
  metaDescription: string;
  description: string;
  bodyMarkdown: string;
  downloadUrl?: string | null;
  /** Audit finding H3: `apps/worker/src/db/schema.ts` documents the author
   * requirement as "enforced at the Zod layer (this function)" for
   * article/case_study content, but no such check existed here — an item
   * could publish with `authorId: null`, and `frontmatter.ts` would then
   * silently omit the `author:` frontmatter key entirely rather than
   * erroring, so the live `.md` file ends up with no author line at all. */
  authorId?: string | null;
  /** The main picture (repo path). Only checked for sites whose layout requires one. */
  titleImageUrl?: string | null;
  /** Optional. Blank is fine (the CMS fills today's date when publishing); a value that is not a readable date is not,
   * because the Astro site fails its whole build on it ("RangeError: Invalid time value"). */
  publishDate?: string | null;
  dateModified?: string | null;
}

const isBlank = (value: string | null | undefined): boolean => !value || value.trim().length === 0;

export function getContentPublishBlockers(item: ContentPublishCheckInput, layout?: SiteLayout | null): string[] {
  // A blog-layout site has its own rules (siteLayout.ts): only the fields its schema has are required.
  if (isBlogLayout(layout)) return blogPublishBlockers(layout, item);
  const blockers: string[] = [];

  if (isBlank(item.title)) blockers.push("Add a title.");
  if (isBlank(item.metaTitle)) blockers.push("Add a search title.");
  if (isBlank(item.metaDescription)) blockers.push("Add a search description.");
  if (isBlank(item.description)) blockers.push("Add a short description under the title.");

  if (item.type === "ebook") {
    if (isBlank(item.downloadUrl)) blockers.push("Add the ebook file.");
  } else {
    if (isBlank(item.bodyMarkdown)) blockers.push("Write the text before publishing.");
    // Ebooks have no byline in the real site's templates, so this is
    // article/case_study only, matching frontmatter.ts's `author:` field.
    if (!item.authorId) blockers.push("Choose an author.");
  }

  const unreadable = (v: string | null | undefined) => !isBlank(v) && Number.isNaN(new Date(v as string).getTime());
  if (unreadable(item.publishDate)) blockers.push("Pick a real date for the publish date.");
  if (unreadable(item.dateModified)) blockers.push("Pick a real date for the date modified.");

  return blockers;
}

/**
 * The ONE authoritative map of allowed content lifecycle transitions —
 * "revise the whole pipeline ... follow most recent industry practices":
 * every status-changing route in apps/worker/src/routes/content.ts (submit,
 * reject, publish, schedule, unschedule, archive) used to enforce its own
 * ad hoc `if (existing.status !== "...")` check, each hand-written and each
 * a separate place a future edit could accidentally loosen (or a new route
 * could simply forget to add one). This table is now the single source of
 * truth for "is X -> Y allowed at all," consulted by every one of those
 * routes via `isValidContentTransition` below, so there is exactly one
 * place to audit the whole state machine instead of six.
 *
 * `published -> published` is intentionally included: it is NOT a status
 * change, it is "publish an edit to already-live content" (re-commit the
 * same status), which the /publish route has always supported and still
 * needs the transition check to allow.
 */
export const CONTENT_STATUS_TRANSITIONS: Record<ContentStatus, ContentStatus[]> = {
  draft: ["pending_review"],
  pending_review: ["draft", "scheduled", "published"],
  scheduled: ["pending_review", "published", "failed"],
  // v2: a failed scheduled publish can be retried (re-scheduled), published
  // by hand, or pulled back into review — never silently stuck.
  failed: ["pending_review", "scheduled", "published"],
  published: ["published", "archived"],
  archived: [],
};

export function isValidContentTransition(from: ContentStatus, to: ContentStatus): boolean {
  return CONTENT_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * Separation-of-duties gate for the review pipeline — client-reported bug:
 * "without manager approval, no content can be published" (stated as the
 * REQUIRED behavior; the actual defect was that this was never enforced).
 *
 * Real defect found: POST /:id/publish and POST /:id/schedule were already
 * correctly gated by `requireCapability("content.publish")` — a manager
 * always has it, and it CAN be granted to a publisher/editor from Settings
 * -> Roles & Permissions — but nothing stopped the same account that
 * authored and submitted a draft from also being the one to click
 * "Approve & publish"/"Schedule" on it, as long as their role/capability
 * allowed it. That is not a second person's review at all — it's the
 * author rubber-stamping their own work — exactly the "four-eyes"
 * separation-of-duties gap every modern editorial workflow (WordPress'
 * editor/author review roles, Contentful's/Sanity's approval workflows)
 * closes by preventing the content's own author from being its approver.
 *
 * `createdBy` (who originally authored this item) is the right field to
 * compare against here, NOT `updatedBy` (whoever saved most recently) —
 * `updatedBy` changes on every PATCH, including a reviewing manager fixing
 * a typo on SOMEONE ELSE's draft before approving it, which must NOT count
 * as "the approver is the author." `createdBy` is nullable (a user account
 * can be deleted, see contentSchema's doc comment) — a null author can
 * never collide with a real acting user id, so this correctly allows it.
 */
export function isSelfApproval(item: { createdBy: string | null }, actingUserId: string): boolean {
  return item.createdBy !== null && item.createdBy === actingUserId;
}

/** Sort options for GET /content (bug fix: the Content List table used to
 * have NO sort control at all, always ordered `updatedAt desc`). Values
 * are `<field>_<direction>`, matching how the admin UI's Select stores
 * them directly as the query param. */
export const contentSortSchema = z.enum([
  "updated_desc",
  "updated_asc",
  "title_asc",
  "title_desc",
  "publishDate_desc",
  "publishDate_asc",
]);
export type ContentSort = z.infer<typeof contentSortSchema>;

/** Query params accepted by GET /content (list with filters).
 *
 * Bug fix ("content listing page ... list should have 'published date
 * newest first' by default"): the default used to be `updated_desc`
 * (last-edited order), which does not match how an editorial team actually
 * scans a content list — they think in terms of "what went live most
 * recently," not "what was last saved" (a typo fix bumps updatedAt without
 * being a meaningful publish event). Default changed to `publishDate_desc`
 * everywhere this value is read: this schema's own `.default()` (used
 * whenever `sort` is omitted from the query string), the worker's
 * `orderByForSort` fallback branch (apps/worker/src/routes/content.ts), and
 * the admin's ContentList.tsx `sort` state fallback — all three must agree,
 * or the URL's implicit "no sort param" state would silently mean something
 * different depending on which of the three read it.
 */
export const listContentQuerySchema = z.object({
  siteId: z.string().min(1).optional(),
  status: contentStatusSchema.optional(),
  type: contentTypeSchema.optional(),
  search: z.string().optional(),
  sort: contentSortSchema.default("publishDate_desc"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListContentQuery = z.infer<typeof listContentQuerySchema>;

export const listContentResponseSchema = z.object({
  items: z.array(contentSchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
  /** Items that came from an approved AI draft, or that had AI help: whether a person changed them afterwards, and what the AI cost (null = no priced call). Items not in the map had no AI. */
  aiInfo: z
    .record(z.object({ drafted: z.boolean().default(true), editedSince: z.boolean(), costUsd: z.number().nullable().default(null), calls: z.number().int().default(0), unpricedCalls: z.number().int().default(0) }))
    .default({}),
});
export type ListContentResponse = z.infer<typeof listContentResponseSchema>;

// ---------------------------------------------------------------------------
// Content versions (full snapshot on every save; supports rollback)
// ---------------------------------------------------------------------------

/** Everything content_versions snapshots as JSON — the same editable field
 * set contentInputSchema covers plus tagIds (tags live in a join table).
 * Also extended with `slug`, since a rollback needs to know what slug the
 * version had even though slug isn't otherwise part of contentInputSchema
 * (it's covered — kept here for clarity as this is the versioned copy). */
export const contentVersionSnapshotSchema = contentInputSchema
  .omit({ siteId: true, type: true })
  .extend({
    tagIds: z.array(z.string()).default([]),
    // Deliberately loosened back from contentInputSchema's slugSchema
    // (audit finding H2): this schema parses a historical JSON snapshot
    // already stored in content_versions on the READ path, not a new
    // client submission — a version snapshot written before slugSchema
    // existed could have a slug that predates the stricter regex, and
    // this must keep parsing it rather than breaking the Version History
    // view for old content. New writes are already constrained at the
    // point they're accepted, via contentInputSchema/updateContentSchema.
    slug: z.string().min(1),
  });
export type ContentVersionSnapshot = z.infer<typeof contentVersionSnapshotSchema>;

/**
 * A row from content_versions. Migration 0002 reworked this table to
 * store one JSON `snapshot` column instead of hand-mirroring every
 * `content` column (the old approach meant every new content field also
 * had to be added here by hand — exactly the kind of drift this rework
 * was asked to eliminate). `snapshot` is parsed against
 * contentVersionSnapshotSchema by the worker before it's ever sent to the
 * admin UI, so the API contract for a version is just as strongly typed
 * as before — only the storage representation changed.
 */
export const contentVersionSchema = z.object({
  id: z.string().min(1),
  contentId: z.string().min(1),
  versionNumber: z.number().int().min(1),
  status: contentStatusSchema,
  snapshot: contentVersionSnapshotSchema,
  /** Nullable slot for a future ad hoc preview branch; not automated in
   * this build (see docs/ARCHITECTURE.md "deferred" section) but present
   * so the schema doesn't need a migration to add preview support later. */
  previewBranch: z.string().nullable(),
  // Nullable for the same reason as content.createdBy above.
  createdBy: z.string().min(1).nullable(),
  createdAt: z.string(),
});
export type ContentVersion = z.infer<typeof contentVersionSchema>;

// ---------------------------------------------------------------------------
// Media (image commit)
// ---------------------------------------------------------------------------

/**
 * Body for POST /media/upload. The actual bytes travel as multipart
 * form-data (see apps/worker/src/routes/media.ts); this schema describes
 * the non-file fields sent alongside the file.
 *
 * `contentType`/`slug` were added once real evidence showed images are
 * organized per content-type in a per-slug subfolder (e.g.
 * src/assets/images/demandhub/articles/<slug>/..., .../case-study/<slug>/...),
 * not flat under one images_base_path as originally assumed — see
 * apps/worker/src/routes/media.ts's `mediaRepoPath()`. Both are optional so
 * a generic/one-off upload (e.g. an author profile picture, which IS flat
 * in the real repo) still works without them.
 */
export const uploadMediaFieldsSchema = z.object({
  siteId: z.string().min(1),
  branch: publishTargetSchema, // which branch's tree to commit the image into
  /** Optional filename override; if omitted the original upload filename is used (sanitized). */
  filename: z.string().min(1).optional(),
  /** Which per-type image folder to nest under (article/case_study/ebook),
   * or "author" for the flat authors/ folder. Omit for a flat upload
   * directly under the site's images_base_path. */
  contentType: z.enum(["article", "case_study", "ebook", "author"]).optional(),
  /** Content slug — required together with `contentType` for
   * article/case_study (whose images live in a per-slug subfolder); not
   * used for ebook/author, whose real image folders are flat. Audit
   * finding H2: this reaches a real repo file path via `mediaRepoPath()`,
   * which already sanitizes the filename but was relying on this being
   * pre-validated — it wasn't. */
  slug: slugSchema.optional(),
});
export type UploadMediaFields = z.infer<typeof uploadMediaFieldsSchema>;

export const uploadMediaResponseSchema = z.object({
  /** Repo-relative path the image will land at, e.g.
   * "src/assets/images/demandhub/articles/my-image.png" — ready to store
   * directly in a content item's image fields. As of the R2 staging
   * redesign this is NOT yet committed to GitHub: it is staged in R2 and
   * only actually lands in the repo when the content item is published
   * (see apps/worker/src/routes/media.ts and routes/content.ts's
   * commitContentToGit). `staged` is always true from this endpoint. */
  path: z.string(),
  staged: z.literal(true),
});
export type UploadMediaResponse = z.infer<typeof uploadMediaResponseSchema>;

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export const auditLogEntrySchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1).nullable(),
  actorUserId: z.string().min(1),
  actorEmail: z.string().email(),
  action: auditActionSchema,
  targetType: auditTargetTypeSchema.nullable(),
  targetId: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string(),
});
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;

export const listAuditLogQuerySchema = z.object({
  siteId: z.string().min(1).optional(),
  action: auditActionSchema.optional(),
  actorUserId: z.string().min(1).optional(),
  targetType: auditTargetTypeSchema.optional(),
  targetId: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListAuditLogQuery = z.infer<typeof listAuditLogQuerySchema>;

export const listAuditLogResponseSchema = z.object({
  items: z.array(auditLogEntrySchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
});
export type ListAuditLogResponse = z.infer<typeof listAuditLogResponseSchema>;

// ---------------------------------------------------------------------------
// Git / deploy status
// ---------------------------------------------------------------------------

export const gitStatusSchema = z.object({
  siteId: z.string().min(1),
  connected: z.boolean(),
  repo: z.string().nullable(),
  productionBranch: z.string(),
  stagingBranch: z.string(),
  /** null if the repo/branch could not be reached (bad PAT, 404, etc.) — see `error`. */
  productionHeadSha: z.string().nullable(),
  stagingHeadSha: z.string().nullable(),
  recentCommits: z.array(
    z.object({
      sha: z.string(),
      message: z.string(),
      author: z.string(),
      date: z.string(),
      branch: publishTargetSchema,
    })
  ),
  error: z.string().nullable(),
});
export type GitStatus = z.infer<typeof gitStatusSchema>;

export const promoteToProductionResponseSchema = z.object({
  mergeCommitSha: z.string().nullable(),
  alreadyUpToDate: z.boolean(),
  deployHookFired: z.boolean(),
});
export type PromoteToProductionResponse = z.infer<typeof promoteToProductionResponseSchema>;

/** Response shape for POST /git/import — see
 * apps/worker/src/lib/importSync.ts for what each count means and the
 * full safety model (additive-only, re-runnable, per-item failures
 * collected rather than fatal). */
export const importSummarySchema = z.object({
  categoriesImported: z.number().int().min(0),
  categoriesSkipped: z.number().int().min(0),
  authorsImported: z.number().int().min(0),
  authorsSkipped: z.number().int().min(0),
  articlesImported: z.number().int().min(0),
  articlesSkipped: z.number().int().min(0),
  caseStudiesImported: z.number().int().min(0),
  caseStudiesSkipped: z.number().int().min(0),
  ebooksImported: z.number().int().min(0),
  ebooksSkipped: z.number().int().min(0),
  errors: z.array(z.object({ item: z.string(), error: z.string() })),
  /** Things worth knowing that are not failures (for example a file name that was cleaned up). */
  notes: z.array(z.string()).default([]),
  /** Posts still waiting for the next run (standard Astro sites import in small batches). */
  remaining: z.number().int().min(0).default(0),
});
export type ImportSummary = z.infer<typeof importSummarySchema>;

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const sessionUserSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  name: z.string().min(1),
  role: roleSchema,
  /** Two-step sign-in is on for this account. */
  twoFactorEnabled: z.boolean().nullish(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

// ---------------------------------------------------------------------------
// Generic API error shape returned by every route on failure
// ---------------------------------------------------------------------------

export const apiErrorSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
